<template>
  <b-modal
    :visible="visible"
    :title="getModalTitle()"
    size="xl"
    hide-footer
    @hidden="$emit('close')"
  >
    <div v-if="participant">
      <!-- Participant info -->
      <div class="participant-header">
        <div class="participant-info">
          <h4 class="participant-name">
            {{ getFullName() }}
          </h4>
          <div class="participant-badges">
            <span class="badge badge-number">#{{ participant.num }}</span>
            <span
              v-if="participant.distance"
              class="badge badge-distance"
            >
              <b-icon
                icon="signpost"
                class="mr-1"
              />
              {{ participant.distance }}
            </span>
            <span
              v-if="participant.group"
              class="badge badge-group"
            >
              <b-icon
                icon="people"
                class="mr-1"
              />
              {{ participant.group }}
            </span>
          </div>
        </div>
        <div class="result-card">
          <div class="result-label">
            Gun Time
          </div>
          <div class="result-value">
            {{ participant.gunTime || participant.result || '—' }}
          </div>
          <div class="chip-time-value">
            Chip: {{ participant.chipTime || '—' }}
          </div>
          <div
            v-if="showGunFallbackInfo"
            class="chip-time-value text-warning"
          >
            Chip: использован gun time
          </div>
        </div>
      </div>

      <!-- Checkpoint results summary -->
      <b-card
        title="По чекпоинтам"
        class="mb-2 summary-card"
      >
        <b-table
          :items="checkpointSummary"
          :fields="summaryFields"
          small
          striped
          responsive
          class="mb-0 summary-table"
        >
          <template #cell(point)="{ item }">
            <strong>{{ item.header || item.point }}</strong>
          </template>

          <template #cell(selectedReading)="{ item }">
            <span
              v-if="item.hasReading"
              class="text-success"
            >
              ✓ {{ item.time }}
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>

          <template #cell(alternatives)="{ item }">
            <b-badge
              v-if="item.alternativesCount > 0"
              variant="info"
              pill
            >
              {{ item.alternativesCount }}
            </b-badge>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>

          <template #cell(usedPoints)="{ item }">
            <span v-if="item.usedPoints && item.usedPoints.length">
              {{ item.usedPoints.join(', ') }}
            </span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              v-if="item.isOverridden"
              variant="warning"
            >
              Переопределено
            </b-badge>
            <b-badge
              v-else-if="item.violates"
              variant="danger"
            >
              Нарушение
            </b-badge>
            <b-badge
              v-else-if="item.hasReading"
              variant="success"
            >
              OK
            </b-badge>
            <span
              v-else
              class="text-muted"
            >Нет отметки</span>
          </template>
        </b-table>
      </b-card>

      <!-- Schematic track flow -->
      <div
        v-if="checkpoints && checkpoints.length > 0"
        class="track-schema-toggle-block"
      >
        <b-button
          variant="outline-primary"
          size="sm"
          class="track-schema-toggle-btn"
          :aria-expanded="showTrackSchema ? 'true' : 'false'"
          @click="showTrackSchema = !showTrackSchema"
        >
          <b-icon
            :icon="showTrackSchema ? 'chevron-up' : 'diagram-3'"
            class="mr-1"
          />
          {{ showTrackSchema ? 'Скрыть схему трассы' : 'Показать схему трассы' }}
          <b-badge
            variant="warning"
            class="ml-2 track-schema-toggle-beta"
          >
            Бета
          </b-badge>
          <b-badge
            variant="secondary"
            class="ml-1 track-schema-toggle-count"
          >
            {{ checkpoints.length }} точ.
          </b-badge>
        </b-button>
        <b-collapse
          v-model="showTrackSchema"
          class="mt-2"
        >
          <track-schema-view
            v-if="showTrackSchema"
            :checkpoints="checkpoints"
            :participant="participant"
          />
        </b-collapse>
      </div>

      <!-- All raw readings -->
      <b-card
        title="Считывания"
        class="mb-0 readings-card"
      >
        <div class="readings-hint text-muted small mb-2">
          <strong>Всего: {{ allReadings.length }}</strong>
          <span
            v-if="participant"
            class="ml-2"
          >
            (исходных считываний: {{ (participant.rawReadings || []).length }},
            в протоколе чекпоинтов: {{ (participant.checkpointResults || []).length }})
          </span>
          <span class="ml-2 d-none d-md-inline">
            — <span class="text-success font-weight-bold">зелёным</span>: в расчёте результата; остальные: не используются (отсеяны).
          </span>
        </div>

        <div
          v-if="allReadings.length === 0"
          class="text-center py-3"
        >
          <div class="text-muted mb-2">
            <b-icon
              icon="info-circle"
              font-scale="1.5"
            />
          </div>
          <div class="text-muted">
            <strong>Нет данных о считываниях</strong>
          </div>
          <small class="text-muted">
            Возможно, участник еще не начал забег или данные не поступили от системы
          </small>
        </div>

        <div
          v-else
          class="readings-timeline"
        >
          <template v-for="(rItem, rIndex) in readingRenderItems">
            <div
              v-if="rItem.kind === 'pre-start-group-header'"
              :key="'pdm-rg-' + rItem.groupId"
              class="reading-group-header"
              :class="{ 'is-expanded': rItem.expanded }"
              role="button"
              tabindex="0"
              @click="togglePreStartGroup(rItem.groupId)"
              @keydown.enter.prevent="togglePreStartGroup(rItem.groupId)"
              @keydown.space.prevent="togglePreStartGroup(rItem.groupId)"
            >
              <div class="reading-group-header__marker">
                <b-icon
                  :icon="rItem.expanded ? 'chevron-down' : 'chevron-right'"
                />
              </div>
              <div class="reading-group-header__content">
                <div class="reading-group-header__title">
                  <strong>Считывания до времени старта участника</strong>
                  <b-badge
                    variant="secondary"
                    class="ml-2"
                  >
                    {{ rItem.count }}
                  </b-badge>
                  <span class="text-muted small ml-2 reading-group-header__range">
                    {{ rItem.firstTs }} … {{ rItem.lastTs }}
                  </span>
                </div>
                <div class="reading-group-header__hint text-muted small">
                  Сгруппированы автоматически, в расчёте результата не участвуют.
                </div>
              </div>
              <div class="reading-group-header__toggle">
                <span class="pdm-details-btn">
                  {{ rItem.expanded ? 'Свернуть' : 'Развернуть' }}
                </span>
              </div>
            </div>
            <div
              v-else
              :key="'pdm-r-' + rItem.idx"
              class="reading-item"
              :class="[getReadingClass(rItem.reading), { 'reading-item--in-group': !!rItem.inGroup }]"
            >
              <div class="reading-marker">
                <span
                  v-if="rItem.reading.inCalculation"
                  class="marker-selected"
                >✓</span>
                <span
                  v-else-if="rItem.reading.isMarker"
                  class="marker-info"
                >●</span>
                <span
                  v-else
                  class="marker-rejected"
                >✗</span>
              </div>

              <div class="reading-content">
                <div class="reading-header-compact">
                  <div class="reading-title-line">
                    <strong
                      class="reading-point"
                      :class="{ 'reading-point--in-calc': rItem.reading.inCalculation }"
                    >{{ getReadingPointTitle(rItem.reading) }}</strong>
                    <b-badge
                      v-if="rItem.reading.lap"
                      variant="secondary"
                      class="ml-1"
                    >
                      Круг {{ rItem.reading.lap }}
                    </b-badge>
                    <b-badge
                      v-if="rItem.reading.isMarker"
                      variant="info"
                      class="ml-1"
                    >
                      Маркер
                    </b-badge>
                    <b-badge
                      v-else-if="rItem.reading.inCalculation"
                      variant="success"
                      class="ml-1 badge-in-calc"
                    >
                      В расчёте
                    </b-badge>
                    <b-badge
                      v-else
                      variant="secondary"
                      class="ml-1"
                    >
                      Отсеяно
                    </b-badge>
                    <span
                      class="reading-ts"
                      :class="{ 'reading-ts--in-calc': rItem.reading.inCalculation }"
                    >{{ rItem.reading.timestamp }}</span>
                  </div>
                  <div class="reading-toolbar">
                    <b-button
                      v-b-toggle="'pdm-reading-detail-' + rItem.idx"
                      size="sm"
                      variant="link"
                      class="pdm-details-btn px-1"
                    >
                      Подробнее
                    </b-button>
                  </div>
                </div>

                <div
                  v-if="shouldShowReason(rItem.reading)"
                  class="reading-reason-compact"
                  :class="{ 'reading-reason-compact--in-calc': rItem.reading.inCalculation }"
                >
                  {{ rItem.reading.reason }}
                </div>

                <b-collapse
                  :id="'pdm-reading-detail-' + rItem.idx"
                  class="reading-collapse"
                >
                  <div
                    v-if="rItem.reading.absoluteTimestamp"
                    class="time-extra"
                  >
                    <div
                      v-if="formatAbsoluteTime(rItem.reading.absoluteTimestamp)"
                      class="time-extra-line"
                    >
                      <span class="time-label">UTC</span>
                      <span>{{ formatAbsoluteTime(rItem.reading.absoluteTimestamp) }}</span>
                    </div>
                    <div
                      v-if="localTime(rItem.reading.absoluteTimestamp)"
                      class="time-extra-line"
                    >
                      <span class="time-label">{{ timezone || 'Локальное' }}</span>
                      <span>{{ localTime(rItem.reading.absoluteTimestamp) }}</span>
                    </div>
                  </div>
                  <div class="meta-grid">
                    <template v-if="rItem.reading.code">
                      <div class="meta-cell">
                        <span class="meta-label">Код чипа</span>
                        <code>{{ rItem.reading.code }}</code>
                      </div>
                    </template>
                    <template v-if="rItem.reading.epc">
                      <div class="meta-cell meta-cell--wide">
                        <span class="meta-label">EPC</span>
                        <code class="small-code">{{ rItem.reading.epc }}</code>
                      </div>
                    </template>
                    <template v-if="rItem.reading.rssi && rItem.reading.rssi !== '0'">
                      <div class="meta-cell">
                        <span class="meta-label">RSSI</span>
                        <b-badge :variant="getRssiVariant(rItem.reading.rssi)">
                          {{ rItem.reading.rssi }} dBm
                        </b-badge>
                      </div>
                    </template>
                    <template v-if="rItem.reading.antena">
                      <div class="meta-cell">
                        <span class="meta-label">Антенна</span>
                        <span>{{ rItem.reading.antena }}</span>
                      </div>
                    </template>
                    <template v-if="rItem.reading.num">
                      <div class="meta-cell">
                        <span class="meta-label">Номер</span>
                        <span>{{ rItem.reading.num }}</span>
                      </div>
                    </template>
                  </div>
                </b-collapse>
              </div>
            </div>
          </template>
        </div>
      </b-card>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BCard,
  BBadge,
  BTable,
  BButton,
  BCollapse,
  BIcon,
  VBTooltip,
  VBToggle,
} from 'bootstrap-vue'
import { formatToLocalDateTime, DEFAULT_TIMEZONE } from '../../utils/timezone'
import { translateReasonCode, translateTimelineDetailsToRu } from '../../utils/reason-codes'
import TrackSchemaView from '../visualization/TrackSchemaView.vue'

/** Same basis as CheckpointEditor — compare cumulative / displayed checkpoint times. */
function parseDurationToMs(str) {
  if (str == null || str === '') return null
  const s = String(str).trim()
  if (!s || s === '—') return null
  const parts = s.split(':').map(p => p.trim())
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10) || 0
    const m = parseInt(parts[1], 10) || 0
    const secParts = parts[2].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return ((h * 60 + m) * 60 + sec) * 1000 + ms
  }
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10) || 0
    const secParts = parts[1].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return (m * 60 + sec) * 1000 + ms
  }
  return null
}

/**
 * When selectedReading is not persisted, rows stay in additionalOptions as "Отсеяно"
 * even if finalize() picked that timestamp for cp.time — mark those rows as used in calc.
 */
function parseReadingInstantMs(ts) {
  if (ts == null || ts === '') return null
  const s = String(ts).trim()
  if (s.includes('T') || (s.length >= 10 && s[4] === '-' && s[7] === '-')) {
    const ms = Date.parse(s)
    return Number.isNaN(ms) ? null : ms
  }
  return parseDurationToMs(s)
}

function normalizePointName(p) {
  return String(p || '').trim().toUpperCase()
}

/** Canonical instant for a chip row (ISO in `timestamp` or `absoluteTimestamp`). */
function readingAbsoluteMs(r) {
  if (!r) return null
  const raw = r.absoluteTimestamp ?? r.timestamp
  const m = parseReadingInstantMs(raw)
  return m
}

const RAW_NOT_USED_REASON = 'Сырое событие не попало в расчёт ни одного чекпоинта (другое имя точки, антенна, порядок прохождения или отфильтровано анализатором). Для перепривязки используйте альтернативные считывания в таблице чекпоинтов.'

function mapRawTimelineReason(raw) {
  const details = String(raw && raw.timelineReasonDetails ? raw.timelineReasonDetails : '').trim()
  const reasonCode = String(raw && raw.timelineReasonCode ? raw.timelineReasonCode : '').trim()
  const fromDetails = details ? translateTimelineDetailsToRu(details) : ''
  if (fromDetails) return fromDetails
  if (reasonCode) {
    const fromCode = translateReasonCode(reasonCode, '')
    if (fromCode) return fromCode
  }
  const reason = String(raw && raw.timelineReason ? raw.timelineReason : '').trim()
  if (!reason) return RAW_NOT_USED_REASON
  const reasonMap = {
    OutOfWindow: 'Считывание вне окна времени участника.',
    Duplicate: 'Дубликат считывания.',
    TooEarly: 'Слишком рано для текущего чекпоинта.',
    TooLate: 'Слишком поздно для текущего чекпоинта.',
    Selected: 'Выбрано анализатором.',
    NoCheckpointMatch: 'Не подошло ни под один чекпоинт по правилам назначения.',
  }
  return reasonMap[reason] || RAW_NOT_USED_REASON
}

function translateSelectedReason(reason, reasonCode) {
  const translatedByCode = translateReasonCode(reasonCode, '')
  if (translatedByCode) return translatedByCode
  if (!reason) return 'Выбрано анализатором'
  const legacyMap = {
    'Manually overridden': 'Переопределено вручную',
    'Selected by analyzer': 'Выбрано анализатором',
    'Virtual reading': 'Виртуальное считывание',
    'Manually set time': 'Время введено вручную',
  }
  if (legacyMap[reason]) return legacyMap[reason]
  const fromEn = translateTimelineDetailsToRu(reason)
  return fromEn || reason
}

function translateAltReason(reason, reasonCode) {
  const translatedByCode = translateReasonCode(reasonCode, '')
  if (translatedByCode) return translatedByCode
  if (!reason) return 'Не выбрано системой'
  const legacyMap = {
    'Too early': 'Слишком рано',
    'Too late': 'Слишком поздно',
    Duplicate: 'Дубликат считывания',
  }
  if (legacyMap[reason]) return legacyMap[reason]
  const fromEn = translateTimelineDetailsToRu(reason)
  return fromEn || reason
}

/**
 * Prefer clock-consistent relative time from checkpoint formatting; keep ISO-derived instant on absoluteTimestamp.
 */
function formatMainLineTimestamp(raw) {
  const s = raw && raw.timestamp != null ? String(raw.timestamp).trim() : ''
  if (!s) return '—'
  if (s.includes('T') || (s.length >= 10 && s[4] === '-' && s[7] === '-')) {
    try {
      const d = new Date(s)
      if (!Number.isNaN(d.getTime())) {
        const hh = String(d.getUTCHours()).padStart(2, '0')
        const mm = String(d.getUTCMinutes()).padStart(2, '0')
        const ss = String(d.getUTCSeconds()).padStart(2, '0')
        const ms = String(d.getUTCMilliseconds()).padStart(3, '0')
        return `${hh}:${mm}:${ss}.${ms}`
      }
    } catch (e) {
      /* fallthrough */
    }
  }
  return s
}

function getOptionDisplayTimestamp(opt, timeDisplayMode = 'split') {
  if (!opt) return '—'
  if (timeDisplayMode === 'cumulative') {
    return opt.timestamp || opt.splitTime || '—'
  }
  return opt.splitTime || opt.timestamp || '—'
}

function optionsFromCheckpoint(cp) {
  const out = []
  if (cp.selectedReading) {
    out.push({ opt: cp.selectedReading, role: 'selected' })
  }
  if (cp.additionalOptions && cp.additionalOptions.length) {
    cp.additionalOptions.forEach(alt => out.push({ opt: alt, role: 'alt' }))
  }
  return out
}

function optAbsoluteMs(opt) {
  if (!opt || !opt.absoluteTimestamp) return null
  const ms = Date.parse(String(opt.absoluteTimestamp))
  return Number.isNaN(ms) ? null : ms
}

function lapMatchesCheckpoint(raw, cpLap) {
  const rawLap = raw.lap || 0
  const cLap = cpLap || 0
  if (raw.lap == null || raw.lap === '') return true
  return cLap === rawLap
}

/**
 * Match raw row to checkpoint option by point + absolute time.
 * Prefer selectedReading over additionalOptions when both match — otherwise a tighter match on an
 * alt ("Too late") could win over a looser selected timestamp and show a contradictory label.
 */
function enrichmentForRawReading(raw, checkpointResults, toleranceMs, timeDisplayMode = 'split') {
  const rawAbs = readingAbsoluteMs(raw)
  const rawPoint = normalizePointName(raw.point)
  if (rawAbs == null || !rawPoint) return null

  let bestSel = null
  let bestSelDelta = Infinity

  for (const cp of checkpointResults) {
    if (normalizePointName(cp.point) !== rawPoint) continue
    if (!lapMatchesCheckpoint(raw, cp.lap)) continue
    const sr = cp.selectedReading
    if (!sr) continue
    const oAbs = optAbsoluteMs(sr)
    if (oAbs == null) continue
    const delta = Math.abs(oAbs - rawAbs)
    if (delta > toleranceMs) continue
    if (delta < bestSelDelta) {
      bestSelDelta = delta
      bestSel = sr
    }
  }

  if (bestSel != null) {
    return {
      displayTimestamp: getOptionDisplayTimestamp(bestSel, timeDisplayMode) || formatMainLineTimestamp(raw),
      reason: translateSelectedReason(bestSel.reason, bestSel.reasonCode),
      isSelectedFromCheckpoint: true,
    }
  }

  let bestAlt = null
  let bestAltDelta = Infinity

  for (const cp of checkpointResults) {
    if (normalizePointName(cp.point) !== rawPoint) continue
    if (!lapMatchesCheckpoint(raw, cp.lap)) continue
    const alts = cp.additionalOptions || []
    for (const opt of alts) {
      const oAbs = optAbsoluteMs(opt)
      if (oAbs == null) continue
      const delta = Math.abs(oAbs - rawAbs)
      if (delta > toleranceMs) continue
      if (delta < bestAltDelta) {
        bestAltDelta = delta
        bestAlt = opt
      }
    }
  }

  if (bestAlt == null) return null

  return {
    displayTimestamp: getOptionDisplayTimestamp(bestAlt, timeDisplayMode) || formatMainLineTimestamp(raw),
    reason: translateAltReason(bestAlt.reason, bestAlt.reasonCode),
    isSelectedFromCheckpoint: false,
  }
}

function rawCoversOption(rawReadings, cpPoint, optAbsMs, toleranceMs) {
  if (optAbsMs == null) return false
  const want = normalizePointName(cpPoint)
  return rawReadings.some(raw => {
    const a = readingAbsoluteMs(raw)
    if (a == null) return false
    if (Math.abs(a - optAbsMs) > toleranceMs) return false
    return normalizePointName(raw.point) === want
  })
}

const ABS_MATCH_SLACK_MS = 2500
/** Pre-gun cluster + small after-gun window for matching CP0 start-arch chip to gun time. */
const START_LINE_GUN_BEFORE_MS = 25 * 60 * 1000
const START_LINE_GUN_AFTER_MS = 2 * 60 * 1000

function isStartLineFirstCheckpoint(t) {
  if (t.cpIndex !== 0) return false
  const h = String(t.header || '').toLowerCase()
  if (h.includes('старт')) return true
  const pt = t.point
  return pt === 'FINISH' || pt.includes('FINISH') || pt === 'START' || pt.includes('START')
}

function applyCheckpointTimeMatching(readings, checkpointResults, effectiveStartMs = null) {
  const crs = checkpointResults || []
  const rowPoint = r => normalizePointName(r.point)
  const rowLap = r => (r.lap || 0)
  const cpLap = cp => (cp.lap || 0)
  const lapCompatible = (cp, r) => (
    cpLap(cp) === rowLap(r) || r.lap == null || r.lap === ''
  )
  const rowDisplayMs = r => parseDurationToMs(String(r.timestamp || '').trim())

  const targets = crs
    .map((cp, idx) => {
      const relMs = parseDurationToMs(String(cp.time || '').trim())
      if (relMs == null) return null
      const selectedAbs = cp.selectedReading && cp.selectedReading.absoluteTimestamp
        ? Date.parse(String(cp.selectedReading.absoluteTimestamp))
        : null
      return {
        cpIndex: idx,
        header: cp.header || '',
        point: normalizePointName(cp.point),
        lap: cpLap(cp),
        relMs,
        selectedAbsMs: Number.isNaN(selectedAbs) ? null : selectedAbs,
      }
    })
    .filter(Boolean)

  const consumedRows = new Set()
  const usedRows = new Set()
  const matchedTargets = new Set()

  const tryClaimBest = (target, scoreFn, maxScore) => {
    let bestIdx = -1
    let bestScore = Infinity
    for (let i = 0; i < readings.length; i++) {
      if (consumedRows.has(i)) continue
      const r = readings[i]
      if (rowPoint(r) !== target.point) continue
      if (!lapCompatible({ lap: target.lap }, r)) continue
      const score = scoreFn(r)
      if (score == null || score > maxScore) continue
      if (score < bestScore) {
        bestScore = score
        bestIdx = i
      }
    }
    if (bestIdx >= 0) {
      consumedRows.add(bestIdx)
      usedRows.add(bestIdx)
      matchedTargets.add(target.cpIndex)
      return true
    }
    return false
  }

  // 0) Start arch (CP0): chip row with instant nearest to gun — fixes wrong highlight when API
  // clamps pre-gun split to "00:00:00.00" and selectedReading.absoluteTimestamp is stale vs finalize().
  if (effectiveStartMs != null) {
    targets.forEach(t => {
      if (!isStartLineFirstCheckpoint(t) || matchedTargets.has(t.cpIndex)) return
      let bestIdx = -1
      let bestDist = Infinity
      for (let i = 0; i < readings.length; i++) {
        if (consumedRows.has(i)) continue
        const r = readings[i]
        if (r.isMarker) continue
        if (rowPoint(r) !== t.point) continue
        if (!lapCompatible({ lap: t.lap }, r)) continue
        const abs = readingAbsoluteMs(r)
        if (abs == null) continue
        if (abs < effectiveStartMs - START_LINE_GUN_BEFORE_MS) continue
        if (abs > effectiveStartMs + START_LINE_GUN_AFTER_MS) continue
        const dist = Math.abs(abs - effectiveStartMs)
        if (dist < bestDist) {
          bestDist = dist
          bestIdx = i
        }
      }
      if (bestIdx >= 0) {
        consumedRows.add(bestIdx)
        usedRows.add(bestIdx)
        matchedTargets.add(t.cpIndex)
      }
    })
  }

  // 1) Strongest match: selected absolute timestamp from backend
  targets.forEach(t => {
    if (matchedTargets.has(t.cpIndex)) return
    if (t.selectedAbsMs == null) return
    tryClaimBest(t, r => {
      const abs = readingAbsoluteMs(r)
      return abs == null ? null : Math.abs(abs - t.selectedAbsMs)
    }, ABS_MATCH_SLACK_MS)
  })

  // 2) Absolute bridge via effective start + cp.time (relative)
  if (effectiveStartMs != null) {
    targets.forEach(t => {
      if (matchedTargets.has(t.cpIndex)) return
      const cpAbsMs = effectiveStartMs + t.relMs
      tryClaimBest(t, r => {
        const abs = readingAbsoluteMs(r)
        return abs == null ? null : Math.abs(abs - cpAbsMs)
      }, ABS_MATCH_SLACK_MS)
    })
  }

  // 3) Fallback: relative display time match (strict)
  targets.forEach(t => {
    if (matchedTargets.has(t.cpIndex)) return
    tryClaimBest(t, r => {
      const rms = rowDisplayMs(r)
      return rms == null ? null : Math.abs(rms - t.relMs)
    }, 80)
  })

  return readings.map((r, idx) => {
    const usedInFinalResult = usedRows.has(idx)
    const timelineOk = r.timelineReason !== 'Duplicate' && r.timelineReason !== 'OutOfWindow'
    return {
      ...r,
      usedInFinalResult,
      // Raw feed: only rows claimed by checkpoint matching show "В расчёте" (ignore enrichment
      // isSelected so stale API selectedReading cannot trump nearest-to-gun for CP0). Synthetics
      // without a raw twin still rely on isSelected.
      inCalculation: !!(
        (usedInFinalResult && timelineOk)
        || (!usedInFinalResult && r.isSelected && r.syntheticOnly && timelineOk)
      ),
    }
  })
}

export default {
  name: 'ParticipantDetailModal',
  components: {
    BModal,
    BCard,
    BBadge,
    BTable,
    BButton,
    BCollapse,
    BIcon,
    TrackSchemaView,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    participant: {
      type: Object,
      default: null,
    },
    checkpoints: {
      type: Array,
      default: () => [],
    },
    userStartTime: {
      type: String,
      default: null,
    },
    globalStartTime: {
      type: String,
      default: null,
    },
    officialTimeMode: {
      type: String,
      default: 'gunTime',
    },
    timeDisplayMode: {
      type: String,
      default: 'split',
    },
    timezone: {
      type: String,
      default: DEFAULT_TIMEZONE,
    },
  },
  data() {
    return {
      showTrackSchema: false,
      expandedPreStartGroups: {},
      preStartGroupMinCount: 3,
    }
  },
  computed: {
    effectiveStartTime() {
      return this.userStartTime || this.globalStartTime || null
    },
    showChipStartMarker() {
      return this.officialTimeMode === 'chipTime' && !!(this.participant && this.participant.startCheckpointTime)
    },
    showGunFallbackInfo() {
      if (!this.participant) return false
      if (this.officialTimeMode !== 'chipTime') return false
      if (this.participant.status !== 'finished' || !this.participant.hasFinish) return false
      if (this.participant.timeSource === 'gun') return true
      return !!this.participant.isGunFallback
    },
    allReadings() {
      if (!this.participant) {
        return []
      }

      const rawReadings = this.participant.rawReadings || []
      const checkpointResults = this.participant.checkpointResults || []
      const tol = ABS_MATCH_SLACK_MS

      const fromRaw = rawReadings.map(raw => {
        const enr = enrichmentForRawReading(raw, checkpointResults, tol, this.timeDisplayMode)
        const canonicalAbs = raw.timestamp
        return {
          ...raw,
          absoluteTimestamp: canonicalAbs,
          timestamp: enr ? enr.displayTimestamp : formatMainLineTimestamp(raw),
          reason: enr ? enr.reason : mapRawTimelineReason(raw),
          isSelected: !!(enr && enr.isSelectedFromCheckpoint),
          fromRawFeed: true,
        }
      })

      const supplements = []
      const supplementKeys = new Set()
      checkpointResults.forEach(cp => {
        if (cp.isOverridden && !cp.selectedReading) {
          const sk = `override|${normalizePointName(cp.point)}|${String(cp.time || cp.displayTime || '')}`
          if (!supplementKeys.has(sk)) {
            supplementKeys.add(sk)
            supplements.push({
              point: cp.point,
              lap: cp.lap,
              timestamp: cp.time || cp.displayTime || '—',
              absoluteTimestamp: undefined,
              code: '',
              epc: '',
              rssi: '0',
              antena: 'manual',
              num: '',
              isSelected: true,
              reason: 'Время введено вручную',
              syntheticOnly: true,
            })
          }
        }

        optionsFromCheckpoint(cp).forEach(({ opt, role }) => {
          const oAbs = optAbsoluteMs(opt)
          if (oAbs != null && rawCoversOption(rawReadings, cp.point, oAbs, tol)) {
            return
          }
          const dedupKey = oAbs != null
            ? `abs|${normalizePointName(cp.point)}|${oAbs}`
            : `rel|${normalizePointName(cp.point)}|${String(opt.timestamp || '')}|${role}`
          if (supplementKeys.has(dedupKey)) return
          supplementKeys.add(dedupKey)

          const reason = role === 'selected'
            ? translateSelectedReason(opt.reason, opt.reasonCode)
            : translateAltReason(opt.reason, opt.reasonCode)
          supplements.push({
            point: opt.point || cp.point,
            lap: cp.lap,
            timestamp: getOptionDisplayTimestamp(opt, this.timeDisplayMode),
            absoluteTimestamp: opt.absoluteTimestamp,
            code: opt.code || '',
            epc: opt.epc || '',
            rssi: opt.rssi != null ? String(opt.rssi) : '0',
            antena: opt.antena || '',
            num: opt.num || '',
            isSelected: role === 'selected',
            reason,
            syntheticOnly: true,
          })
        })
      })

      const merged = [...fromRaw, ...supplements]
      const effectiveStartMs = this.effectiveStartTime ? Date.parse(this.effectiveStartTime) : null
      const withCalc = applyCheckpointTimeMatching(merged, checkpointResults, Number.isNaN(effectiveStartMs) ? null : effectiveStartMs)
      const startMarkers = []
      const markerKeys = new Set()
      const addMarker = marker => {
        const key = `${marker.point}|${marker.absoluteTimestamp || marker.timestamp}|${marker.reason || ''}`
        if (markerKeys.has(key)) return
        markerKeys.add(key)
        startMarkers.push(marker)
      }

      if (this.userStartTime) {
        addMarker({
          point: 'START',
          lap: 0,
          timestamp: formatMainLineTimestamp({ timestamp: this.userStartTime }),
          absoluteTimestamp: this.userStartTime,
          code: '',
          epc: '',
          rssi: '0',
          antena: 'config',
          num: this.participant.num || '',
          isSelected: false,
          inCalculation: false,
          usedInFinalResult: false,
          isMarker: true,
          reason: 'Старт участника из конфига',
        })
      }

      if (this.globalStartTime) {
        addMarker({
          point: 'START',
          lap: 0,
          timestamp: formatMainLineTimestamp({ timestamp: this.globalStartTime }),
          absoluteTimestamp: this.globalStartTime,
          code: '',
          epc: '',
          rssi: '0',
          antena: 'config',
          num: this.participant.num || '',
          isSelected: false,
          inCalculation: false,
          usedInFinalResult: false,
          isMarker: true,
          reason: 'Глобальный старт из конфига',
        })
      }

      if (this.showChipStartMarker && this.participant.startCheckpointTime) {
        const chipRelMs = parseDurationToMs(String(this.participant.startCheckpointTime).trim())
        const chipAbs = (effectiveStartMs != null && chipRelMs != null)
          ? new Date(effectiveStartMs + chipRelMs).toISOString()
          : undefined
        const chipAbsMs = chipAbs ? Date.parse(chipAbs) : null
        const mergeSlackMs = 1200
        const existingIdx = withCalc.findIndex(row => {
          const rowAbs = readingAbsoluteMs(row)
          if (chipAbsMs != null && rowAbs != null) {
            return Math.abs(rowAbs - chipAbsMs) <= mergeSlackMs
          }
          if (chipRelMs != null) {
            const rowRelMs = parseDurationToMs(String(row.timestamp || '').trim())
            return rowRelMs != null && Math.abs(rowRelMs - chipRelMs) <= 120
          }
          return false
        })

        if (existingIdx >= 0) {
          const existing = withCalc[existingIdx]
          const existingReason = String(existing.reason || '').trim()
          const shouldReplaceReason = !existingReason
            || existingReason === RAW_NOT_USED_REASON
            || existingReason.includes('Сырое событие не попало в расчёт')
          withCalc[existingIdx] = {
            ...existing,
            isMarker: true,
            markerKind: 'chip-start',
            reason: shouldReplaceReason
              ? 'Старт по отметке старта (chip time)'
              : `${existingReason}; Старт по отметке старта (chip time)`,
          }
        } else {
          addMarker({
            point: 'START',
            lap: 0,
            timestamp: this.participant.startCheckpointTime,
            absoluteTimestamp: chipAbs,
            code: '',
            epc: '',
            rssi: '0',
            antena: 'chip-time',
            num: this.participant.num || '',
            isSelected: false,
            inCalculation: false,
            usedInFinalResult: false,
            isMarker: true,
            markerKind: 'chip-start',
            reason: 'Старт по отметке старта (chip time)',
          })
        }
      }

      return [...withCalc, ...startMarkers].sort((a, b) => {
        const ta = readingAbsoluteMs(a) ?? parseDurationToMs(String(a.timestamp || '').trim()) ?? 0
        const tb = readingAbsoluteMs(b) ?? parseDurationToMs(String(b.timestamp || '').trim()) ?? 0
        return ta - tb
      })
    },

    /**
     * Flat list of items to render in the "Считывания" timeline. Runs of
     * consecutive pre-start readings longer than `preStartGroupMinCount` are
     * collapsed into a single toggleable group header. All other readings
     * keep their original single-item rendering.
     */
    readingRenderItems() {
      const src = this.allReadings
      const items = []
      const minGroup = Math.max(2, this.preStartGroupMinCount || 3)
      let i = 0
      while (i < src.length) {
        const reading = src[i]
        if (this.isPreStartReading(reading)) {
          const run = [{ reading, idx: i }]
          let j = i + 1
          while (j < src.length && this.isPreStartReading(src[j])) {
            run.push({ reading: src[j], idx: j })
            j += 1
          }
          if (run.length > minGroup) {
            const groupId = `pre-start-${run[0].idx}`
            const expanded = !!this.expandedPreStartGroups[groupId]
            items.push({
              kind: 'pre-start-group-header',
              groupId,
              count: run.length,
              firstTs: run[0].reading.timestamp,
              lastTs: run[run.length - 1].reading.timestamp,
              expanded,
            })
            if (expanded) {
              run.forEach(({ reading: r, idx }) => {
                items.push({
                  kind: 'reading', reading: r, idx, inGroup: groupId,
                })
              })
            }
          } else {
            run.forEach(({ reading: r, idx }) => {
              items.push({ kind: 'reading', reading: r, idx })
            })
          }
          i = j
        } else {
          items.push({ kind: 'reading', reading, idx: i })
          i += 1
        }
      }
      return items
    },

    checkpointSummary() {
      if (!this.participant || !this.participant.checkpointResults) {
        return []
      }

      return this.participant.checkpointResults.map(cp => {
        const tDisplay = (this.timeDisplayMode === 'cumulative'
          ? [cp.time, cp.displayTime, cp.splitTime]
          : [cp.displayTime, cp.splitTime, cp.time]
        )
          .map(x => (x != null ? String(x).trim() : ''))
          .find(s => s !== '' && s !== '—')
        const hasReading = !!cp.isOverridden
          || !!(tDisplay && tDisplay !== '—')
        return {
          point: cp.point,
          header: cp.header,
          lap: cp.lap,
          time: tDisplay || cp.time || '—',
          hasReading,
          alternativesCount: cp.additionalOptions ? cp.additionalOptions.length : 0,
          usedPoints: Array.isArray(cp.usedPoints) ? cp.usedPoints.filter(Boolean) : [],
          isOverridden: cp.isOverridden,
          violates: cp.violatesTimeConstraint,
        }
      })
    },

    summaryFields() {
      return [
        { key: 'point', label: 'Чекпоинт' },
        { key: 'selectedReading', label: 'Время' },
        { key: 'alternatives', label: 'Альт.' },
        { key: 'usedPoints', label: 'Использованные точки' },
        { key: 'status', label: 'Статус' },
      ]
    },
  },
  watch: {
    visible(v) {
      if (!v) {
        this.showTrackSchema = false
        this.expandedPreStartGroups = {}
      }
    },
    participant() {
      this.showTrackSchema = false
      this.expandedPreStartGroups = {}
    },
  },
  methods: {
    getFullName() {
      if (!this.participant) return ''

      const name = this.participant.name || ''
      const secondName = this.participant.secondName || ''

      return secondName && name ? `${secondName} ${name}` : name || secondName || 'Участник'
    },

    getModalTitle() {
      return `Детальный просмотр: ${this.getFullName()}`
    },

    getReadingClass(reading) {
      return {
        'is-marker': !!reading.isMarker,
        'is-selected': reading.inCalculation,
        'is-rejected': !reading.inCalculation && !reading.isMarker,
      }
    },

    getReadingPointTitle(reading) {
      const point = String((reading && reading.point) || '').trim() || '—'
      if (!reading || !reading.isMarker) return point
      return `${point} / Маркер старта`
    },

    shouldShowReason(reading) {
      if (!reading || !reading.reason) return false
      if (reading.isMarker) return true
      if (!reading.inCalculation) return true

      // Hide rejection reasons on rows that are already in final calculation.
      return ![
        'Слишком рано',
        'Слишком поздно',
        'Не выбрано системой',
        RAW_NOT_USED_REASON,
      ].includes(String(reading.reason))
    },

    isPreStartReading(reading) {
      if (!reading || reading.isMarker || reading.inCalculation) return false
      const code = String(reading.timelineReasonCode || reading.reasonCode || '').trim()
      if (code === 'OUT_OF_WINDOW_BEFORE_START') return true
      const r = String(reading.reason || '').trim().toLowerCase()
      if (!r) return false
      return r.startsWith('считывание раньше времени старта')
        || r.startsWith('считывание до времени старта')
    },

    togglePreStartGroup(groupId) {
      this.$set(
        this.expandedPreStartGroups,
        groupId,
        !this.expandedPreStartGroups[groupId],
      )
    },

    getRssiVariant(rssi) {
      const rssiValue = parseInt(rssi, 10)

      if (rssiValue > -60) return 'success'
      if (rssiValue > -70) return 'info'
      if (rssiValue > -80) return 'warning'
      return 'danger'
    },

    formatAbsoluteTime(timestamp) {
      if (!timestamp) return ''

      try {
        const date = new Date(timestamp)
        const year = date.getUTCFullYear()
        const month = String(date.getUTCMonth() + 1).padStart(2, '0')
        const day = String(date.getUTCDate()).padStart(2, '0')
        const hours = String(date.getUTCHours()).padStart(2, '0')
        const minutes = String(date.getUTCMinutes()).padStart(2, '0')
        const seconds = String(date.getUTCSeconds()).padStart(2, '0')
        const ms = String(date.getUTCMilliseconds()).padStart(3, '0')

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`
      } catch (e) {
        return timestamp
      }
    },

    localTime(timestamp) {
      if (!timestamp) return ''

      if (typeof timestamp === 'string' && timestamp.match(/^\d{2}:\d{2}:\d{2}/)) {
        return ''
      }

      return formatToLocalDateTime(timestamp, this.timezone)
    },
  },
}
</script>

<style scoped>
.participant-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  background: linear-gradient(118deg, rgba(115, 103, 240, 0.05), rgba(115, 103, 240, 0.02));
  border-radius: 0.358rem;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(115, 103, 240, 0.1);
}

.participant-info {
  flex: 1;
  min-width: 0;
}

.participant-name {
  margin: 0 0 0.35rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #3f3b4f;
}

.participant-badges {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 0.358rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

.badge-number {
  background: linear-gradient(118deg, #7367f0, #9e95f5);
  color: #fff;
  font-weight: 600;
}

.badge-distance {
  background: rgba(0, 207, 232, 0.12);
  color: #00cfe8;
  border: 1px solid rgba(0, 207, 232, 0.2);
}

.badge-group {
  background: rgba(130, 134, 139, 0.12);
  color: #82868b;
  border: 1px solid rgba(130, 134, 139, 0.2);
}

.result-card {
  text-align: right;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border-radius: 0.358rem;
  border: 1px solid rgba(115, 103, 240, 0.25);
  flex-shrink: 0;
}

.result-label {
  font-size: 0.75rem;
  color: #7c7a88;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.result-value {
  font-size: 1.45rem;
  font-weight: 700;
  color: #5e50ce;
  line-height: 1.2;
}

.chip-time-value {
  font-size: 0.8125rem;
  color: #5e5873;
  margin-top: 0.15rem;
}

.readings-card >>> .card-header,
.summary-card >>> .card-header {
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #3f3b4f;
}

.readings-card >>> .card-body {
  padding-top: 0.65rem;
}

.summary-card >>> .card-body {
  padding-top: 0.5rem;
}

.readings-hint {
  line-height: 1.45;
  font-size: 0.875rem;
  color: #5e5873;
}

.readings-hint strong {
  color: #3f3b4f;
  font-weight: 600;
}

.readings-timeline {
  overflow: visible;
}

.reading-item {
  display: flex;
  gap: 0.5rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.358rem;
  margin-bottom: 0.4rem;
  border: 1px solid #ebe9f1;
  background: #fff;
  transition: box-shadow 0.15s ease;
}

.reading-item.is-selected {
  background: linear-gradient(
    90deg,
    rgba(40, 199, 111, 0.14) 0%,
    rgba(40, 199, 111, 0.06) 48%,
    #fff 100%
  );
  border: 1px solid #28c76f;
  border-left: 4px solid #1e9c5e;
  box-shadow: 0 0 0 1px rgba(40, 199, 111, 0.15);
}

.reading-item.is-selected:hover {
  box-shadow: 0 2px 8px rgba(40, 199, 111, 0.18);
}

.reading-item.is-rejected {
  background: #fafafa;
  border-color: #e8e8e8;
}

.reading-item.is-marker {
  background: linear-gradient(
    90deg,
    rgba(0, 207, 232, 0.12) 0%,
    rgba(0, 207, 232, 0.05) 48%,
    #fff 100%
  );
  border: 1px solid rgba(0, 207, 232, 0.4);
  border-left: 4px solid #00b8d4;
}

.reading-item:hover {
  box-shadow: 0 1px 6px rgba(34, 41, 47, 0.06);
}

.reading-item--in-group {
  background: #fbfbfc;
  border-color: #ececf2;
  border-left: 3px solid #d7d4e2;
  margin-left: 1.25rem;
}

.reading-group-header {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  margin-bottom: 0.4rem;
  border: 1px dashed #d7d4e2;
  border-left: 3px solid #b7b3c6;
  border-radius: 0.358rem;
  background: #f8f8fa;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.reading-group-header:hover {
  background: #f1f0f6;
  border-color: #b7b3c6;
}

.reading-group-header:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.25);
}

.reading-group-header.is-expanded {
  background: #f1f0f6;
  border-style: solid;
}

.reading-group-header__marker {
  flex-shrink: 0;
  width: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.1rem;
  color: #6e6b7b;
}

.reading-group-header__content {
  flex: 1;
  min-width: 0;
}

.reading-group-header__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #3f3b4f;
  line-height: 1.3;
}

.reading-group-header__range {
  font-family: "SF Mono", Menlo, Consolas, monospace;
  white-space: nowrap;
}

.reading-group-header__hint {
  margin-top: 0.15rem;
  line-height: 1.35;
}

.reading-group-header__toggle {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 0.1rem;
}

.reading-group-header__toggle .pdm-details-btn {
  color: #7367f0;
  font-size: 0.8125rem;
  font-weight: 500;
}

.reading-marker {
  flex-shrink: 0;
  width: 26px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.1rem;
}

.marker-selected {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.12);
  border: 1px solid #28c76f;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
}

.marker-rejected {
  color: #b9b9c3;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid #ddd;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.marker-info {
  color: #00cfe8;
  background: rgba(0, 207, 232, 0.12);
  border: 1px solid rgba(0, 207, 232, 0.45);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
}

.reading-content {
  flex: 1;
  min-width: 0;
}

.reading-header-compact {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.35rem;
}

.reading-title-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.reading-point {
  color: #3f3b4f;
  font-size: 0.9375rem;
  font-weight: 600;
}

.reading-point--in-calc {
  color: #1e7e4e;
}

.reading-ts {
  font-weight: 600;
  color: #5e50ce;
  margin-left: 0.25rem;
  font-size: 1rem;
}

.reading-ts--in-calc {
  color: #17805a;
}

.reading-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-shrink: 0;
}

.pdm-details-btn {
  font-size: 0.875rem;
  font-weight: 500;
}

.reading-reason-compact {
  margin-top: 0.3rem;
  padding: 0.4rem 0.5rem;
  background: #eef0f4;
  border-radius: 0.3rem;
  border-left: 3px solid #7367f0;
  font-size: 0.875rem;
  color: #3f3b4f;
  line-height: 1.45;
}

.reading-reason-compact--in-calc {
  background: rgba(40, 199, 111, 0.1);
  border-left-color: #1e9c5e;
  color: #1a4d30;
}

.badge-in-calc {
  font-weight: 600;
}

.reading-collapse {
  margin-top: 0.35rem;
}

.time-extra {
  margin-bottom: 0.35rem;
}

.time-extra-line {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  flex-wrap: wrap;
  font-size: 0.8125rem;
}

.time-extra-line + .time-extra-line {
  margin-top: 0.25rem;
}

.time-extra-line > span:not(.time-label) {
  color: #2e2c35;
  font-weight: 500;
  font-size: 0.8125rem;
}

.time-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #5e5873;
  letter-spacing: 0.03em;
  min-width: 7rem;
  flex-shrink: 0;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  gap: 0.45rem 0.6rem;
  padding: 0.55rem 0.65rem;
  background: #f6f7f9;
  border-radius: 0.35rem;
  border: 1px solid #e2e0ea;
}

.meta-cell {
  min-width: 0;
}

.meta-cell--wide {
  grid-column: 1 / -1;
}

.meta-label {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: #5e5873;
  margin-bottom: 0.15rem;
  font-weight: 600;
}

.meta-cell > span:not(.meta-label) {
  color: #2e2c35;
  font-size: 0.875rem;
}

.meta-cell >>> .badge {
  font-size: 0.8125rem;
}

.meta-grid code {
  background: #fff;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  border: 1px solid #dcd8e8;
  color: #2e2c35;
  font-family: 'Courier New', monospace;
}

.meta-grid .small-code {
  font-size: 0.8125rem;
  word-break: break-all;
}

.summary-table >>> tbody td,
.summary-table >>> thead th {
  padding: 0.4rem 0.55rem;
  vertical-align: middle;
  font-size: 0.875rem;
  color: #3f3b4f;
}

.summary-table >>> thead th {
  font-weight: 600;
  color: #5e5873;
}

.track-schema-toggle-block {
  margin-bottom: 0.75rem;
}

.track-schema-toggle-btn {
  font-weight: 500;
}

.track-schema-toggle-beta {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.7rem;
}
</style>
